import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

export default function AdminProfile() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [infoSuccess, setInfoSuccess] = useState("");
  const [infoError, setInfoError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/profile`, {
          credentials: "include", // send HttpOnly cookie
        });

        if (!res.ok) throw new Error("Not authenticated");

        const data = await res.json();
        setEmail(data.email);
        setName(data.name);
      } catch (err) {
        console.error("Failed to load admin profile:", err);
        navigate("/admin-login");
      }
    };

    fetchAdminProfile();
  }, [BASE_URL, navigate]);


  const handleInfoUpdate = async () => {
    if (!name || !email) {
      setInfoError("Name and email are required.");
      setTimeout(() => setInfoError(""), 3000);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/admin/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name }),
      });

      if (!res.ok) throw new Error("Update failed");
      setInfoSuccess("Profile updated successfully!");
      setTimeout(() => setInfoSuccess(""), 3000);
    } catch (err) {
      setInfoError("Failed to update profile.");
      setTimeout(() => setInfoError(""), 3000);
      console.error(err);
    }
  };


  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill out all password fields.");
      setTimeout(() => setPasswordError(""), 3000);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      setTimeout(() => setPasswordError(""), 3000);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/admin/profile/password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Password update failed");
      }

      setPasswordSuccess("Password updated successfully. Logging out...");

      setTimeout(() => {
        navigate("/admin-login");
      }, 3000);
    } catch (err) {
      setPasswordError(err.message);
      console.error("Password update error:", err);
    }
  };


  return (
    <div className="w-full max-w-4xl mx-auto bg-white p-6 md:p-8 rounded shadow">
      <h2 className="text-2xl font-bold mb-6">Edit Profile</h2>

      {/* Update Name & Email */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold mb-4">Profile Info</h3>

        {infoSuccess && (
          <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-4">
            {infoSuccess}
          </div>
        )}

        {infoError && (
          <div className="bg-red-100 text-red-700 text-sm px-3 py-2 rounded mb-4">
            {infoError}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Email <span className="text-red-600">*</span>
          </label>
          <input
            type="email"
            value={email}
            className="w-full border rounded px-3 py-2 bg-gray-100"
            disabled
          />
        </div>

        <button
          onClick={handleInfoUpdate}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
        >
          Update Info
        </button>
      </div>

      {/* Change Password */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Change Password</h3>

        {passwordSuccess && (
          <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-4">
            {passwordSuccess}
          </div>
        )}

        {passwordError && (
          <div className="bg-red-100 text-red-700 text-sm px-3 py-2 rounded mb-4">
            {passwordError}
          </div>
        )}

        {/* Current Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Current Password <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              required
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-2.5 text-gray-600"
            >
              {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            New Password <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              value={newPassword}
              required
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-2.5 text-gray-600"
            >
              {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">
            Confirm New Password <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              required
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-2.5 text-gray-600"
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          onClick={handlePasswordUpdate}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500"
        >
          Update Password
        </button>
      </div>
    </div>
  );
}
